import axios from 'axios';
import { domainImageUrl, toSameOriginImageProxyUrl } from '../../utils/imageProxy';
import { ensureSignedProxyUrls } from '../../utils/signedImageProxyCache';

/** Gallery photos of an article (`article_photo` + `article_photo_image`). */

function appendCrop(formData, { originalFile, croppedFile, croppedArea, aspectRatio, cropShape, rotation }) {
  formData.append('croppedAreaPixels', JSON.stringify(croppedArea ?? {}));
  formData.append('aspectRatio', String(aspectRatio ?? 1));
  formData.append('cropShape', cropShape ?? 'rect');
  formData.append('rotation', String(rotation ?? 0));
  if (originalFile) {
    formData.append('originalFile', originalFile);
  }
  formData.append('croppedFile', croppedFile);
  return formData;
}

export async function createArticlePhoto(articleItemId, prepared, { title, sortOrder } = {}) {
  const formData = new FormData();
  formData.append('articleItemId', articleItemId);
  formData.append('title', title || prepared.croppedFile?.name || 'Photo');
  if (sortOrder != null) {
    formData.append('sort_order', String(sortOrder));
  }
  appendCrop(formData, prepared);
  return axios.post('article-photo', formData);
}

export async function updateArticlePhotoImage(photoId, prepared) {
  return axios.post(`article-photo/${photoId}/image`, appendCrop(new FormData(), prepared));
}

export async function updateArticlePhotoMetadata(photoId, metadata) {
  return axios.patch(`article-photo/${photoId}`, metadata);
}

export async function deleteArticlePhoto(photoId) {
  return axios.delete(`article-photo/${photoId}`);
}

/**
 * Blob URL of the uncropped source, so the editor reopens on the original pixels.
 * Same rationale as `resolveEntityImageEditorSource`: the SPA and the API are different
 * origins on AWS, so the image must be fetched by axios rather than handed to the cropper.
 */
export async function fetchArticlePhotoSource(photo) {
  const sourceKey = photo?.image?.key_source;
  if (!sourceKey) {
    return null;
  }

  await ensureSignedProxyUrls([sourceKey], { silent: true });
  const imageSrc = domainImageUrl(photo.image, { size: 'source' });
  if (!imageSrc) {
    return null;
  }

  const response = await axios.get(toSameOriginImageProxyUrl(imageSrc), {
    responseType: 'blob',
    skipConnectivityGuard: true,
  });
  const blob = response?.data;
  if (!(blob instanceof Blob) || blob.type?.includes('text/html')) {
    return null;
  }
  return URL.createObjectURL(blob);
}
